import { apiClient, setTokens, clearTokens } from './client';
import type { TelegramAuthData, VkAuthData, AuthResponse, UserInfo } from '@/types';

/**
 * API модуль для аутентификации
 * @contract Telegram OAuth + JWT refresh
 */

/**
 * Аутентификация через Telegram Login Widget
 * @param data Данные от Telegram Widget
 * @returns AuthResponse с токенами и информацией о пользователе
 */
export async function loginWithTelegram(data: TelegramAuthData): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/telegram', data);

  // Сохраняем токены
  setTokens(response.data.accessToken, response.data.refreshToken);

  return response.data;
}

/**
 * Аутентификация через VK ID (OAuth 2.1 PKCE)
 *
 * @param data code + device_id + code_verifier + state от @vkid/sdk
 * @returns AuthResponse с токенами
 */
export async function loginWithVk(data: VkAuthData): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/vk', data);
  setTokens(response.data.accessToken, response.data.refreshToken);
  return response.data;
}

/**
 * Платформа для code-auth flow.
 */
export type AuthCodePlatform = 'vk' | 'telegram';

/**
 * Типизированная ошибка backend'а code-auth flow.
 *
 * @contract Все ошибки sendAuthCode/verifyAuthCode бросают CodeAuthError.
 *           UI-код должен смотреть на `error.code` (string enum) вместо message.
 */
export class CodeAuthError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly details: Record<string, unknown>,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'CodeAuthError';
  }
}

interface CodeAuthErrorBody {
  code?: string;
  message?: string;
  retryAfterSec?: number;
  attemptsLeft?: number;
  platform?: AuthCodePlatform;
}

function throwCodeAuthError(err: unknown): never {
  const axErr = err as {
    response?: { status?: number; data?: CodeAuthErrorBody };
  };
  const status = axErr?.response?.status ?? 0;
  const body = axErr?.response?.data ?? {};
  throw new CodeAuthError(
    body.code ?? 'UNKNOWN',
    status,
    body as Record<string, unknown>,
    body.message,
  );
}

/**
 * Отправить код авторизации на указанную платформу.
 *
 * @throws CodeAuthError — .code in USER_NOT_FOUND | RATE_LIMITED | BOT_BLOCKED | NOTIFICATION_UNAVAILABLE
 */
export async function sendAuthCode(
  platform: AuthCodePlatform,
  platformUserId: string,
): Promise<{ sent: true; resendAfterSec: number }> {
  try {
    const response = await apiClient.post<{ sent: true; resendAfterSec: number }>(
      '/auth/code/send',
      { platform, platformUserId },
    );
    return response.data;
  } catch (err) {
    throwCodeAuthError(err);
  }
}

/**
 * Проверить код и получить JWT.
 *
 * @throws CodeAuthError — .code in CODE_INVALID | CODE_EXPIRED | BLOCKED
 */
export async function verifyAuthCode(
  platform: AuthCodePlatform,
  platformUserId: string,
  code: string,
): Promise<AuthResponse> {
  try {
    const response = await apiClient.post<AuthResponse>('/auth/code/verify', {
      platform,
      platformUserId,
      code,
    });
    setTokens(response.data.accessToken, response.data.refreshToken);
    return response.data;
  } catch (err) {
    throwCodeAuthError(err);
  }
}

/**
 * Обновление токенов
 * @param refreshToken Refresh token
 * @returns Новые токены
 */
export async function refreshTokens(refreshToken: string): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/refresh', { refreshToken });

  // Сохраняем новые токены
  setTokens(response.data.accessToken, response.data.refreshToken);

  return response.data;
}

/**
 * Получить информацию о текущем пользователе
 * @returns UserInfo
 */
export async function getCurrentUser(): Promise<UserInfo> {
  const response = await apiClient.get<UserInfo>('/auth/me');
  return response.data;
}

/**
 * Выход из системы
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } catch {
    // Игнорируем ошибки при logout
  } finally {
    clearTokens();
  }
}

/**
 * Получить демо пользователя для локальной разработки
 * @returns AuthResponse с токенами
 */
export async function getDemoUser(): Promise<AuthResponse> {
  // Используем Vite proxy -> user-panel (порт 3012)
  const response = await fetch('/api/v1/auth/demo-user');
  if (!response.ok) {
    throw new Error('Failed to get demo user');
  }
  const data = await response.json();

  // UP-24 (2026-05-26): синхронизируем токены через setTokens helper —
  // он обновляет И localStorage И module-level accessToken/refreshToken,
  // которые читает response interceptor. Старый код писал только в
  // localStorage, оставляя module-level state = null → на 401 interceptor
  // моментально редиректил на /login (см. UP-24 root cause в CHANGELOG).
  setTokens(data.accessToken, data.refreshToken);

  return data;
}

/**
 * Получить локального пользователя для локальной разработки
 * @returns AuthResponse с токенами
 */
export async function getLocalUser(): Promise<AuthResponse> {
  // Используем Vite proxy -> user-panel (порт 3012)
  const response = await fetch('/api/v1/auth/local-user');
  if (!response.ok) {
    throw new Error('Failed to get local user');
  }
  const data = await response.json();

  // UP-24 (2026-05-26): см. getDemoUser выше — те же причины перейти на setTokens.
  setTokens(data.accessToken, data.refreshToken);

  return data;
}
